'use client';

import React, { useEffect, useRef, useState } from 'react';

const STEPS = [
  {
    number: '01',
    title: 'Run the CLI',
    desc: 'Install once globally and run the init command. Answer a few prompts about your stack.',
    code: `$ npm install -g nishu-backend

$ nishu-backend init my-saas-api

? Database: MongoDB / PostgreSQL
? Auth: JWT + Refresh Tokens
? Payments: Razorpay + Stripe
? Jobs: BullMQ + Redis
? Logging: Pino (structured)

Scaffolding project...`,
    highlights: [
      { word: 'nishu-backend init', color: '#5B8CFF' },
      { word: 'Scaffolding project...', color: '#22D3A0' },
    ],
  },
  {
    number: '02',
    title: 'Configure your env',
    desc: 'A single .env.example with every variable documented. Zod validates on startup — no silent misconfigs.',
    code: `# .env (auto-generated)
PORT=3000
NODE_ENV=development

# Database
MONGO_URI=mongodb://localhost:27017/mydb

# Auth
JWT_SECRET=your-secret-here
JWT_REFRESH_SECRET=your-refresh-secret
JWT_EXPIRES_IN=15m
JWT_REFRESH_EXPIRES_IN=7d

# Payments
RAZORPAY_KEY_ID=rzp_live_xxx
STRIPE_WEBHOOK_SECRET=whsec_xxx

# Redis
REDIS_URL=redis://localhost:6379`,
    highlights: [],
  },
  {
    number: '03',
    title: 'Build your product',
    desc: 'Every module is wired up. Add your business logic to the service layer and ship.',
    code: `$ npm run dev
[info] Server running on :3000
[info] MongoDB connected
[info] Redis connected
[info] BullMQ workers started

# Your endpoints are ready:
GET  /health          → 200 OK
POST /api/v1/auth/login
POST /api/v1/auth/refresh
POST /api/v1/payments/webhook
GET  /api/v1/users (RBAC: admin)

# Build for production:
$ docker compose up --build`,
    highlights: [
      { word: '[info]', color: '#22D3A0' },
      { word: '200 OK', color: '#22D3A0' },
    ],
  },
];

function CodeReveal({ code, isActive }: { code: string; isActive: boolean }) {
  const [visibleLines, setVisibleLines] = useState(0);
  const lines = code.split('\n');

  useEffect(() => {
    if (!isActive) {
      setVisibleLines(0);
      return;
    }
    setVisibleLines(0);
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setVisibleLines(i);
      if (i >= lines.length) clearInterval(interval);
    }, 60);
    return () => clearInterval(interval);
  }, [isActive, lines.length]);

  return (
    <div data-component-id="src/app/home/components/HowItWorksSection.tsx:97:4" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="97" data-component-end-line="129" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22code-block%20h-full%22%7D" className="code-block h-full">
      <div data-component-id="src/app/home/components/HowItWorksSection.tsx:98:6" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="98" data-component-end-line="102" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22code-header%22%7D" className="code-header">
        <span data-component-id="src/app/home/components/HowItWorksSection.tsx:99:8" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="99" data-component-end-line="99" data-component-file="HowItWorksSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22code-dot%20code-dot-red%22%7D" className="code-dot code-dot-red" />
        <span data-component-id="src/app/home/components/HowItWorksSection.tsx:100:8" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="100" data-component-end-line="100" data-component-file="HowItWorksSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22code-dot%20code-dot-yellow%22%7D" className="code-dot code-dot-yellow" />
        <span data-component-id="src/app/home/components/HowItWorksSection.tsx:101:8" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="101" data-component-end-line="101" data-component-file="HowItWorksSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22code-dot%20code-dot-green%22%7D" className="code-dot code-dot-green" />
      </div>
      <pre data-component-id="src/app/home/components/HowItWorksSection.tsx:103:6" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="103" data-component-end-line="128" data-component-file="HowItWorksSection.tsx" data-component-name="pre" data-component-content="%7B%22elementName%22%3A%22pre%22%2C%22className%22%3A%22p-5%20text-%5B12px%5D%20font-mono%20leading-relaxed%20overflow-auto%20h-%5Bcalc(100%25-40px)%5D%22%7D" className="p-5 text-[12px] font-mono leading-relaxed overflow-auto h-[calc(100%-40px)]">
        {lines.slice(0, visibleLines).map((line, i) => {
          const isComment = line.trim().startsWith('#');
          const isPrompt = line.startsWith('$');
          const isInfo = line.includes('[info]');
          return (
            <div data-component-id="src/app/home/components/HowItWorksSection.tsx:109:12" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="109" data-component-end-line="122" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22whitespace-pre%22%7D"
              key={i}
              className="whitespace-pre"
              style={{
                color: isComment
                  ? '#4A5270'
                  : isPrompt
                  ? '#22D3A0'
                  : isInfo
                  ? '#5B8CFF' :'#F0F4FF',
              }}
            >
              {line || ' '}
            </div>
          );
        })}
        {isActive && visibleLines < lines.length && (
          <span data-component-id="src/app/home/components/HowItWorksSection.tsx:126:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="126" data-component-end-line="126" data-component-file="HowItWorksSection.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22cursor-blink%20text-%5B%235B8CFF%5D%22%2C%22textContent%22%3A%22%7C%22%7D" className="cursor-blink text-[#5B8CFF]">|</span>
        )}
      </pre>
    </div>
  );
}

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -30px 0px' }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Trigger code reveal when step becomes active
  useEffect(() => {
    const stepObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = stepRefs.current.indexOf(entry.target as HTMLDivElement);
            if (idx !== -1) setActiveStep(idx);
          }
        });
      },
      { threshold: 0.5 }
    );

    stepRefs.current.forEach((el) => el && stepObserver.observe(el));
    return () => stepObserver.disconnect();
  }, []);

  return (
    <section data-component-id="src/app/home/components/HowItWorksSection.tsx:175:4" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="175" data-component-end-line="240" data-component-file="HowItWorksSection.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22how-it-works%22%2C%22className%22%3A%22py-28%20px-6%20relative%20bg-%5B%230F1524%5D%22%7D" id="how-it-works" ref={sectionRef} className="py-28 px-6 relative bg-[#0F1524]">
      <div data-component-id="src/app/home/components/HowItWorksSection.tsx:176:6" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="176" data-component-end-line="176" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20dot-grid%20opacity-20%20pointer-events-none%22%7D" className="absolute inset-0 dot-grid opacity-20 pointer-events-none" />

      <div data-component-id="src/app/home/components/HowItWorksSection.tsx:178:6" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="178" data-component-end-line="239" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%22%7D" className="max-w-7xl mx-auto">
        <div data-component-id="src/app/home/components/HowItWorksSection.tsx:179:8" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="179" data-component-end-line="187" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-20%20animate-on-scroll%22%7D" className="text-center mb-20 animate-on-scroll">
          <div data-component-id="src/app/home/components/HowItWorksSection.tsx:180:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="180" data-component-end-line="180" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22section-label%20mb-4%22%2C%22textContent%22%3A%22%2F%2F%20How%20It%20Works%22%7D" className="section-label mb-4">// How It Works</div>
          <h2 data-component-id="src/app/home/components/HowItWorksSection.tsx:181:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="181" data-component-end-line="183" data-component-file="HowItWorksSection.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-4xl%20md%3Atext-5xl%20font-bold%20tracking-tight%20text-%5B%23F0F4FF%5D%20mb-4%22%2C%22textContent%22%3A%22From%20zero%20to%20production%20in%203%20steps.%22%7D" className="text-4xl md:text-5xl font-bold tracking-tight text-[#F0F4FF] mb-4">
            From zero to production in 3 steps.
          </h2>
          <p data-component-id="src/app/home/components/HowItWorksSection.tsx:184:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="184" data-component-end-line="186" data-component-file="HowItWorksSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B%238B95B0%5D%20text-lg%20max-w-xl%20mx-auto%22%2C%22textContent%22%3A%22No%20tutorials.%20No%20boilerplate%20hunting.%20Just%20run%2C%20configure%2C%20and%20build.%22%7D" className="text-[#8B95B0] text-lg max-w-xl mx-auto">
            No tutorials. No boilerplate hunting. Just run, configure, and build.
          </p>
        </div>

        <div data-component-id="src/app/home/components/HowItWorksSection.tsx:189:8" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="189" data-component-end-line="238" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20lg%3Agrid-cols-2%20gap-12%20items-start%22%7D" className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Steps */}
          <div data-component-id="src/app/home/components/HowItWorksSection.tsx:191:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="191" data-component-end-line="228" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-0%22%7D" className="space-y-0">
            {STEPS.map((step, i) => (
              <div data-component-id="src/app/home/components/HowItWorksSection.tsx:193:14" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="193" data-component-end-line="226" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-on-scroll%20relative%20pl-14%20pb-12%22%7D"
                key={step.number}
                ref={(el) => { stepRefs.current[i] = el; }}
                className="animate-on-scroll relative pl-14 pb-12"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {/* Step line */}
                {i < STEPS.length - 1 && (
                  <div data-component-id="src/app/home/components/HowItWorksSection.tsx:201:18" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="201" data-component-end-line="201" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20left-5%20top-12%20bottom-0%20w-px%20bg-gradient-to-b%20from-%5Brgba(91%2C140%2C255%2C0.4)%5D%20to-transparent%22%7D" className="absolute left-5 top-12 bottom-0 w-px bg-gradient-to-b from-[rgba(91,140,255,0.4)] to-transparent" />
                )}

                {/* Step number circle */}
                <div data-component-id="src/app/home/components/HowItWorksSection.tsx:205:16" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="205" data-component-end-line="213" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
                  className={`absolute left-0 top-0 w-10 h-10 rounded-full flex items-center justify-center text-[12px] font-mono font-bold transition-all duration-300 ${
                    activeStep === i
                      ? 'bg-[#5B8CFF] text-white shadow-[0_0_20px_rgba(91,140,255,0.5)]'
                      : 'glass-card text-[#4A5270] border border-[rgba(255,255,255,0.08)]'
                  }`}
                >
                  {step.number}
                </div>

                <div data-component-id="src/app/home/components/HowItWorksSection.tsx:215:16" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="215" data-component-end-line="225" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22cursor-pointer%22%7D"
                  className="cursor-pointer"
                  onClick={() => setActiveStep(i)}
                >
                  <h3 data-component-id="src/app/home/components/HowItWorksSection.tsx:219:18" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="219" data-component-end-line="221" data-component-file="HowItWorksSection.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%7D" className={`text-[18px] font-semibold mb-2 transition-colors duration-200 ${activeStep === i ? 'text-[#F0F4FF]' : 'text-[#8B95B0]'}`}>
                    {step.title}
                  </h3>
                  <p data-component-id="src/app/home/components/HowItWorksSection.tsx:222:18" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="222" data-component-end-line="224" data-component-file="HowItWorksSection.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-%5B13px%5D%20text-%5B%238B95B0%5D%20leading-relaxed%20max-w-sm%22%7D" className="text-[13px] text-[#8B95B0] leading-relaxed max-w-sm">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Code preview */}
          <div data-component-id="src/app/home/components/HowItWorksSection.tsx:231:10" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="231" data-component-end-line="237" data-component-file="HowItWorksSection.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-on-scroll%20sticky%20top-24%20h-%5B420px%5D%22%7D" className="animate-on-scroll sticky top-24 h-[420px]" style={{ transitionDelay: '200ms' }}>
            <CodeReveal data-component-id="src/app/home/components/HowItWorksSection.tsx:232:12" data-component-path="src/app/home/components/HowItWorksSection.tsx" data-component-line="232" data-component-end-line="236" data-component-file="HowItWorksSection.tsx" data-component-name="CodeReveal" data-component-content="%7B%22elementName%22%3A%22CodeReveal%22%7D"
              key={activeStep}
              code={STEPS[activeStep].code}
              isActive={true}
            />
          </div>
        </div>
      </div>
    </section>
  );
}